import {Button, Dropdown, Menu, Select, Tag as AntdTag} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { Link, useParams } from 'react-router-dom';
import useBackofficeFeedbackDetails from './useBackofficeFeedbackDetails';
import styles from './BackofficeFeedbackDetails.module.scss';
import Icon from '../../components/Icon/Icon';
import { FormattedMessage, useIntl } from 'react-intl';
import Tag from '../../components/Tag/Tag';
import moment from 'moment';
import { FeedbackTypes, User } from './BackofficeFeedbackDetails.model';
import { useAuthenticationDataContext } from '../../Helpers/AuthenticationDataProvider';
import { useRef, useState } from 'react';
import ProfileCard from '../../components/Cards/ProfileCard';
import ViewAttachedFiles from "../../components/ViewAttachedFiles/ViewAttachedFiles";

const BackofficeFeedbackDetails = () => {
	const { id } = useParams<{ id: string }>();
	const { getFullName, imageId } = useAuthenticationDataContext();
	const [message, setMessage] = useState<string>('');

	const intl = useIntl();
	const replyInputRef = useRef<any>();

	const {
		activateFeedback,
		archiveFeedback,
		assignFeedback,
		feedbackReply,
		navigateTo,
		isAssignedAdminDeleted,
		isVideoFeedback,
		reassignFeedback,
		resolveFeedback,
		state,
		sendToSpam,
		toggleReply,
		togglePopup
	} = useBackofficeFeedbackDetails({ id });

	const { admins, feedback, reply, openPopup } = state;
	const { userId } = useAuthenticationDataContext();

	const { createdBy } = feedback;

	const getTagColorByRole = (role: string) => {
		switch (role) {
			case 'Mentee':
				return 'grey';
			case 'Mentor':
				return 'blue';
			case 'Admin':
				return 'red';
		}
	};

	const renderUserInfo = (user: User | undefined) => {
		return (
			<div className={styles.user} onClick={!user?.isDeleted ? navigateTo.bind(null, `/users/${user && user.userId}`) : undefined}>
				<div className={styles.iconContainer}>
					{user?.imageId && !user.isDeleted ? (
						<img alt="" src={user?.imageId} className={styles.avatar} />
					) : (
						<div className={styles.avatar}>
							<Icon color="text" height="12px" name="user" width="12px" />
						</div>
					)}
				</div>
				<div className={styles.info}>
					<div>
						{!user?.isDeleted ?`${user?.firstName} ${user?.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}
					</div>
					<Tag color={getTagColorByRole(user?.role || '')}>
						{user?.role || ''}
					</Tag>
				</div>
			</div>
		);
	};

	const getIconByType = () => {

		const type = feedback?.resourceReported?.resourceType;

		if (type === 'Text')
			return (
				<Icon
					name="file-pdf"
					width="12px"
					height="12px"
					color="gray"
					type="solid"
				/>
			);
		if (type === 'Audio')
			return (
				<Icon
					name="microphone"
					width="12px"
					height="12px"
					color="gray"
					type="solid"
				/>
			);
		if (type === 'Video')
			return (
				<Icon name="video" width="12px" height="12px" color="gray" type="solid" />
			);
		return <Icon name="image" width="12px" height="12px" color="gray" />
	};
	
	const renderChips = (chips: string[], category?: string) => {
		return chips.map((field: string, index: number) => (<AntdTag
			key={`${field}${index}`}
			style={{
				border: 'none',
				textTransform: 'uppercase'
			}}>{category ? <FormattedMessage id={`${category}.${field}`} /> : field}</AntdTag>))
	}

	const renderProfileCard = () => (
		<ProfileCard
			actions={[
				<div className={styles.messagesViaEmail}>
					<span><FormattedMessage id="feedback.messagesAreSendViaEmail" /></span>
				</div>
			]}
			customCover={
				<div className={`${styles.profileCardCover} ${feedback?.feedbackType &&
					getClassByFeedbackType(feedback.feedbackType)
					}`}>

					{feedback?.feedbackType && (
						<div className={styles.icon}>
							<Icon
								name="comment-alt-edit"
								width="64px"
								height="64px"
								color="white"
								type="solid"
							/>
						</div>
					)}
				</div>
			}
			fitContainer
			data={{ name: '' }}
		>
			<div className={styles.pading35Px}>
				<h2 className={styles.feedbackLabel}>
					<FormattedMessage id={`feedback.types.${feedback?.feedbackType}`} />
				</h2>
				{renderUserInfo(createdBy)}
				<div>
					<p className={styles.underReview}>
						<FormattedMessage id="feedback.topics" />
					</p>
					{renderChips(feedback?.topics || [], 'feedback.topics')}
				</div>
				{feedback?.userReported ? (
					<>
						<p className={styles.underReview}>
							<FormattedMessage id="feedback.underReview" />
						</p>
						{renderUserInfo(feedback?.userReported)}
					</>
				) : (
					<></>
				)}
				{feedback?.resourceReported ? <>
					<p className={styles.underReview}>
						<FormattedMessage id="feedback.underReview" />
					</p>
					<div className={styles.user} >
						<div className={styles.iconContainer}>
							<div className={styles.avatar}>
								{getIconByType()}
							</div>
						</div>
						<div className={styles.info}>
							<span>{feedback?.resourceReported?.title}</span>
						</div>
					</div>
				</> : <></>}
			</div>
		</ProfileCard>
	);

	const renderAssignSelect = () => (
		<Select
			className={styles.select}
			value = {
				feedback.assignedTo ?
					!isAssignedAdminDeleted(feedback.assignedTo.userId)
								? feedback.assignedTo.userId
								: intl.formatMessage({ id: 'userLabels.deletedUser' })
							: undefined
					}
			onChange={feedback.assignedTo ? reassignFeedback : assignFeedback}
			placeholder={
				<div className={styles.placeholder}>
					<FormattedMessage id="ratings.assignNow" />
				</div>
			}
		>
			{admins?.map((admin) => {
				return (
					<Select.Option value={admin.userId} key={admin.userId}>
						<div className={styles.option}>
							<div className={styles.avatar}>
								{admin.imageId && !admin.isDeleted ? (
									<img src={admin.imageId} alt="" />
								) : (
									<Icon color="light" height="8px" name="user" width="8px" />
								)}
							</div>
							<span>
								{!admin.isDeleted ?  `${admin.firstName} ${admin.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}
							</span>
						</div>
					</Select.Option>
				);
			})}
		</Select>
	);

	const getClassByFeedbackType = (feedbackType: FeedbackTypes) => {
		switch (feedbackType) {
			case 'BugReport':
				return styles.purpleBackground;
			case 'FlagOfAbuse':
				return styles.orangeBackground;
			default:
				return styles.cararraBackground;
		}
	};

	const returnToState = (): number => {
		const { state } = feedback;
		if (state === 'Active') return 1;
		if (state === 'Archived') return 2;
		if (state === 'Spam') return 3;
		return 0;
	};

	const renderDropdown = () => {
		const { state } = feedback;
		if (state === 'Active') {
			return (
				<Menu className={styles.menu}>
					<Menu.Item key="1">
						<Button
							type="link"
							onClick={(e) => {
								archiveFeedback();
							}}
						>
							<FormattedMessage id="buttons.archive" />
						</Button>
					</Menu.Item>
					<Menu.Item key="2">
						<Button
							type="link"
							onClick={(e) => {
								sendToSpam();
							}}
						>
							<FormattedMessage id="buttons.spam" />
						</Button>
					</Menu.Item>
				</Menu>
			);
		}
		if (state === 'Archived') {
			return (
				<Menu className={styles.menu}>
					<Menu.Item key="1">
						<Button
							type="link"
							onClick={(e) => {
								activateFeedback();
							}}
						>
							<FormattedMessage id="buttons.unarchive" />
						</Button>
					</Menu.Item>
					<Menu.Item key="2">
						<Button
							type="link"
							onClick={(e) => {
								sendToSpam();
							}}
						>
							<FormattedMessage id="buttons.spam" />
						</Button>
					</Menu.Item>
				</Menu>
			);
		}
		if (state === 'Spam') {
			return (
				<Menu className={styles.menu}>
					<Menu.Item key="1">
						<Button
							type="link"
							onClick={(e) => {
								activateFeedback();
							}}
						>
							<FormattedMessage id="buttons.unspam" />
						</Button>
					</Menu.Item>
				</Menu>
			);
		}
		return <></>;
	};

	const renderActionButtons = () => {
		return (
			<div className={styles.actionButtons}>
				{renderAssignSelect()}
				{feedback.status !== "Completed" && feedback?.assignedTo?.userId === userId &&
					<Button
						size="large"
						className={styles.approve}
						onClick={resolveFeedback}
					>
						<FormattedMessage id={'buttons.resolve'} />
					</Button>}
				<Dropdown overlay={renderDropdown} trigger={['click']}>
					<Button className={styles.ellipsisButton}>
						<Icon name={'ellipsis-h'} color={'light'} />
					</Button>
				</Dropdown>
			</div>
		);
	};

	const getBreadcrumbsLink = () => {
		switch (feedback.feedbackType) {
			case FeedbackTypes.VideoFeedback: return {label: "navigation.videoFeedback", path: '/video-feedback'};
			case FeedbackTypes.Help: return {label: "navigation.help", path: '/help-requests'};
			default: return {label: "navigation.feedback", path: '/feedbacks'};
		}
	}

	const renderStars = (rating: number) => {
		const ratings = [1, 2, 3, 4, 5];
		return ratings.map(i => {
			if (i <= rating) return <span><Icon name="star" type="solid" color="orange" width="16px" height="16px" /></span>
			return <span><Icon name="star" type="light" color="orange" width="16px" height="16px" /></span>
		})
	}

	const renderTabs = () => {
		const breadcrumbs = getBreadcrumbsLink();
		return (
			<div className={styles.actions}>
				<div className={styles.breadcrumbs}>
					<Link
						to={{
							pathname: breadcrumbs.path,
							state: { activeTab: returnToState() },
						}}
					>
						<FormattedMessage id={breadcrumbs.label} /> /
					</Link>
					{!isVideoFeedback() ?
						<span>{feedback.shortDescription}</span> :
						<div className={styles.starsWrapper}>{feedback.shortDescription && renderStars(parseInt(feedback.shortDescription))}</div>
						}
				</div>
				{!isVideoFeedback() && renderActionButtons()}
			</div>
		);
	};

	const renderReplyWithNoMessage = () => {
		if (reply) {
			return (
				<div className={styles.overflowAuto}>
					<div className={`${styles.userPhoto} ${styles.floatLeft}`}>
						{imageId ? (
							<div className={styles.iconContainer}>
								<img alt="" src={imageId} className={styles.avatar} />
							</div>
						) : (
							<div className={styles.iconContainer}>
								<div className={styles.avatar}>
									<Icon color="text" height="12px" name="user" width="12px" />
								</div>
							</div>
						)}
					</div>
					<div>
						<span style={{ font: 'normal normal 600 16px TheSerif' }}>
							{getFullName()}
						</span>
						<div>
							<TextArea
								className={styles.inputReply}
								ref={replyInputRef}
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								autoSize={{ maxRows: 6, minRows: 6 }}
								placeholder={intl.formatMessage({ id: `formPlaceholders.feedback` })}
								rows={6}
							/>
						</div>
					</div>
					<div className={styles.floatRight}>
						<Button
							style={{ marginRight: '10px' }}
							onClick={() => {
								setMessage('');
								toggleReply();
							}}
						>
							<FormattedMessage id="buttons.cancel" />
						</Button>
						<Button
							type={'primary'}
							onClick={() => {
								feedbackReply(message);
								setMessage('');
								toggleReply();
							}}
							disabled={message.length < 5}
						>
							<FormattedMessage id="buttons.send" />
						</Button>
					</div>
				</div>
			);
		} else {
			return (
				<div className={styles.overflowAuto}>
					{!createdBy?.isDeleted &&
						<Button
							className={styles.floatRight}
							type={'default'}
							onClick={() => {
								toggleReply();
								// replyInputRef.current.focus();
							}}
						>
							<FormattedMessage id="buttons.reply" />
						</Button>
					}
				</div>
			);
		}
	};

	const renderReplyWithMessage = () => {
		const { message, repliedAt, repliedBy } = feedback;
		return (
			<div className={styles.overflowAuto}>
				<div className={`${styles.iconContainer} ${styles.floatLeft}`}>
					{repliedBy?.imageId && !repliedBy.isDeleted ? (
						<img alt="" src={repliedBy.imageId} className={styles.avatar} />
					) : (
						<div className={styles.avatar}>
							<Icon color="text" height="12px" name="user" width="12px" />
						</div>
					)}
				</div>
				<div className={`${styles.details} ${styles.floatLeft}`}>
					<span className={styles.username}>
						{!repliedBy?.isDeleted ? `${repliedBy?.firstName} ${repliedBy?.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}
					</span>
					<p>{message}</p>
					<span className={styles.createdAt}>
						{moment(repliedAt).fromNow()}
					</span>
				</div>
			</div>
		);
	};

	const downloadAll = () => {
		feedback?.attachments?.forEach((url) => {
			window.open(url)
		})
	};

	return (
		<div className={styles.pageContainer}>
			<div
				className={`${styles.pageSider} ${styles.topBorder} ${styles.noPadding} ${styles.fitContent}`}
			>
				{renderProfileCard()}
			</div>
			<div className={styles.pageContent}>
				<div
					className={`${styles.mobileTopper} ${styles.topBorder} ${styles.noPadding}`}>
					{renderProfileCard()}
				</div>
				<div>{renderTabs()}</div>
				<div style={{ padding: '24px' }}>
					<div style={{ marginTop: '15px' }}>
						<span className={styles.textColor}>
							<FormattedMessage id="feedback.createdBy" />
						</span>{' '}
						<span>
							{!createdBy?.isDeleted ? `${createdBy?.firstName} ${createdBy?.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}{' '}
						</span>
						<span className={styles.textColor}>
							<FormattedMessage id="basics.on" />
						</span>{' '}
						<span className={styles.applicationDate}>
							{feedback?.createdAt &&
							moment(feedback?.createdAt).format("DD MMM YYYY")}{' '}
						</span>
					</div>
					<div className={styles.feedbackDetailsWrapper}>
						<div className={styles.feedbackDetailsContainer}>
							<div className={styles.feedbackDetails}>
								<div className={styles.feedbackResponseOwner}>
									<div className={styles.iconContainer}  onClick={!createdBy?.isDeleted? navigateTo.bind(null, `/users/${createdBy?.userId}`): undefined}>
										{createdBy?.imageId && !createdBy.isDeleted? (
											<img
												alt=""
												src={createdBy.imageId}
												className={styles.avatar}
											/>
										) : (
											<div className={styles.avatar}>
												<Icon
													color="text"
													height="12px"
													name="user"
													width="12px"
												/>
											</div>
										)}
									</div>
								</div>
								<div className={styles.details}>
									<span className={styles.username}>
										{!createdBy?.isDeleted ? `${createdBy?.firstName} ${createdBy?.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}
									</span>
									<p>{feedback.description}</p>
									<span className={styles.createdAt}>
										{moment(feedback?.createdAt).fromNow()}
									</span>
								</div>
							</div>
							{feedback?.attachments && feedback?.attachments?.length > 0 &&
								<div className={styles.overflowAuto} style={{ marginTop: '15px', paddingLeft: "65px" }}>
									<div className={styles.floatLeft}>
										<h3><FormattedMessage id="feedback.attachment" /></h3>
										<span>{feedback?.attachments?.length} <FormattedMessage id="feedback.files" /></span>
									</div>
									<div className={styles.floatRight} style={{ display: "flex" }}>
										<Button style={{ marginRight: '15px' }} onClick={togglePopup}><FormattedMessage id="buttons.preview" /></Button>
										<span onClick={downloadAll} style={{ alignSelf: "center", cursor: "pointer" }}><Icon name={'download'} width="25px" height="25px" color="light" /></span>
									</div>
									{openPopup && <ViewAttachedFiles attachments={feedback?.attachments || []} onClose={togglePopup} />}
								</div>}
						</div>

						<div className={styles.feedbackResponse}>
							{!isVideoFeedback() && (feedback.repliedBy
								? renderReplyWithMessage()
								: renderReplyWithNoMessage())}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
export default BackofficeFeedbackDetails;